import { useState, useEffect } from 'react';
import { axios } from '../App';
import { toast } from 'sonner';
import { Brain, AlertTriangle, Users, TrendingUp, Zap, Shield, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function BehaviorAnalysis() {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    loadPatterns();
    const interval = setInterval(loadPatterns, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadPatterns = async () => {
    try {
      const { data } = await axios.get('/behavior-patterns');
      setPatterns(data);
    } catch (error) {
      toast.error('Failed to load behavior patterns');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await axios.post('/behavior-patterns/analyze');
      toast.success(`Analysis complete: ${data.patterns_found} patterns found`);
      loadPatterns();
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const patternIcons = {
    swarm_attack: Users,
    evasion_maneuver: TrendingUp,
    autonomous_behavior: Brain,
    formation_flight: Users,
    zigzag_pattern: Activity,
    altitude_variation: TrendingUp,
    speed_variation: Zap
  };

  const patternColors = {
    swarm_attack: 'red',
    evasion_maneuver: 'amber',
    autonomous_behavior: 'purple',
    formation_flight: 'orange',
    zigzag_pattern: 'yellow',
    altitude_variation: 'blue',
    speed_variation: 'cyan'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sky-500 font-mono text-sm">LOADING BEHAVIOR ANALYSIS...</div>
      </div>
    );
  }

  const criticalPatterns = patterns.filter(p => p.severity === 'critical');
  const highPatterns = patterns.filter(p => p.severity === 'high');
  const totalDetections = patterns.reduce((sum, p) => sum + p.detection_ids.length, 0);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase">AI Behavior Analysis</h1>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
            Machine learning pattern recognition
          </p>
        </div>
        <Button
          onClick={handleAnalyze}
          disabled={analyzing}
          className="bg-purple-600 hover:bg-purple-500 text-white border border-purple-400/20 rounded-none uppercase text-xs"
        >
          <Brain size={16} className={`mr-2 ${analyzing ? 'animate-pulse' : ''}`} />
          {analyzing ? 'Analyzing...' : 'Run Analysis'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-zinc-950/80 border border-red-800 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="text-red-500" size={24} />
            <span className="text-xs font-mono text-zinc-500 uppercase">Critical Patterns</span>
          </div>
          <div className="text-3xl font-bold text-red-500">{criticalPatterns.length}</div>
        </div>
        <div className="bg-zinc-950/80 border border-amber-800 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="text-amber-500" size={24} />
            <span className="text-xs font-mono text-zinc-500 uppercase">High Priority</span>
          </div>
          <div className="text-3xl font-bold text-amber-500">{highPatterns.length}</div>
        </div>
        <div className="bg-zinc-950/80 border border-purple-800 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <Brain className="text-purple-500" size={24} />
            <span className="text-xs font-mono text-zinc-500 uppercase">Total Patterns</span>
          </div>
          <div className="text-3xl font-bold text-purple-500">{patterns.length}</div>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-2xl font-semibold tracking-tight uppercase">Detected Patterns</h2>
        {patterns.length === 0 ? (
          <div className="text-center py-12 text-zinc-600 font-mono text-sm">
            No behavior patterns detected
          </div>
        ) : (
          patterns.map((pattern) => {
            const PatternIcon = patternIcons[pattern.pattern_type] || Brain;
            const colorClass = patternColors[pattern.pattern_type] || 'purple';
            
            return (
              <div
                key={pattern.id}
                className={`bg-zinc-950/80 border rounded-sm p-6 hover:border-zinc-700 transition-colors ${
                  pattern.severity === 'critical'
                    ? 'border-red-800'
                    : pattern.severity === 'high'
                    ? 'border-amber-800'
                    : 'border-zinc-800'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-16 h-16 bg-${colorClass}-500/10 border border-${colorClass}-500/30 rounded-sm flex items-center justify-center flex-shrink-0`}>
                    <PatternIcon size={32} className={`text-${colorClass}-500`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-xl font-bold uppercase tracking-tight">
                          {pattern.pattern_type.replace(/_/g, ' ')}
                        </h3>
                        <p className="text-xs text-zinc-500 mt-1">
                          Confidence: {(pattern.pattern_confidence * 100).toFixed(0)}% • 
                          Drones: {pattern.detection_ids.length}
                        </p>
                      </div>
                      <span
                        className={`text-xs font-mono uppercase px-3 py-2 rounded-sm ${
                          pattern.severity === 'critical'
                            ? 'bg-red-900/50 text-red-300 border border-red-800'
                            : pattern.severity === 'high'
                            ? 'bg-amber-900/50 text-amber-300 border border-amber-800'
                            : pattern.severity === 'medium'
                            ? 'bg-yellow-900/50 text-yellow-300 border border-yellow-800'
                            : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                        }`}
                      >
                        {pattern.severity}
                      </span>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-bold text-emerald-500 mb-2 uppercase">Description</h4>
                        <p className="text-sm text-zinc-300 bg-zinc-900/50 p-3 rounded-sm border border-zinc-800">
                          {pattern.description}
                        </p>
                      </div>

                      <div>
                        <h4 className="text-sm font-bold text-sky-500 mb-2 uppercase">Behavioral Indicators</h4>
                        <div className="flex flex-wrap gap-2">
                          {pattern.indicators.map((indicator, idx) => (
                            <span
                              key={idx}
                              className="text-xs px-3 py-1 bg-sky-900/30 text-sky-300 border border-sky-800 rounded-sm font-mono"
                            >
                              {indicator}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-bold text-amber-500 mb-2 uppercase flex items-center gap-2">
                          <Shield size={16} />
                          Recommended Action
                        </h4>
                        <p className="text-sm text-amber-200 bg-amber-900/20 p-3 rounded-sm border border-amber-800">
                          {pattern.recommended_action}
                        </p>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
                        <div className="text-xs text-zinc-600">
                          <span className="font-mono">Involved Drones: </span>
                          <span className="text-zinc-500">{pattern.detection_ids.join(', ')}</span>
                        </div>
                        <div className="text-xs text-zinc-600">
                          {new Date(pattern.analyzed_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="bg-zinc-950/80 border border-purple-800 rounded-sm p-6">
        <h3 className="text-lg font-bold uppercase mb-4 text-purple-500">AI Pattern Recognition Capabilities</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <h4 className="font-bold text-red-500">SWARM ATTACK DETECTION</h4>
            <p className="text-zinc-400">Identifies coordinated movements of 3+ drones in formation patterns, indicating potential swarm attack tactics.</p>
          </div>
          <div className="space-y-2">
            <h4 className="font-bold text-amber-500">EVASION MANEUVERS</h4>
            <p className="text-zinc-400">Detects sudden trajectory changes, zigzag patterns, and unpredictable movements suggesting autonomous evasion.</p>
          </div>
          <div className="space-y-2">
            <h4 className="font-bold text-purple-500">AUTONOMOUS BEHAVIOR</h4>
            <p className="text-zinc-400">Recognizes AI-controlled flight patterns and decision-making behaviors in drone operations.</p>
          </div>
          <div className="space-y-2">
            <h4 className="font-bold text-orange-500">FORMATION FLIGHT</h4>
            <p className="text-zinc-400">Identifies geometric arrangements and coordinated positioning between multiple drones.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
